import {
    useState, useMemo, useEffect, useRef
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import Modal from 'components/Modal/Modal';
import {
    Box, Text, Button, Flex, Link, Icon
} from 'components/ui';
import RichText from 'components/Content/RichText';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import RecapBasketEN from 'components/Content/Recap/RecapBasket/locales/RecapBasket_en_US';
import RecapBasketFR from 'components/Content/Recap/RecapBasket/locales/RecapBasket_fr_CA';
import {
    colors, fontSizes, space, mediaQueries, radii, lineHeights, borders
} from 'style/config';

import basketSelector from 'selectors/basket/basketSelector';
import { refreshBasket } from 'actions/RwdBasketActions';
import promoUtils from 'utils/Promos';

const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(RecapBasketEN, RecapBasketFR);

const OVERFLOW_PX_TOLERANCE = 1;
const LEGAL_TEXT_ID = 'rtco-offer-details-legal';

function hasRichTextBody(content) {
    return Boolean(content?.json?.content?.length);
}

function hasNonThrowingError(result) {
    const hasErrorMessages = Array.isArray(result?.errorMessages) && result.errorMessages.length > 0;

    return Boolean(result?.errorMessage) || hasErrorMessages;
}

function RtcoOfferDetailsModal({ isOpen, onDismiss, rtcoPromoData }) {
    const [isLegalExpanded, setIsLegalExpanded] = useState(false);
    const [isLegalExpandable, setIsLegalExpandable] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [isApplied, setIsApplied] = useState(false);
    const [error, setError] = useState(null);

    const dispatch = useDispatch();
    const basket = useSelector(basketSelector);
    const legalRef = useRef(null);

    const disclaimerContent = useMemo(() => {
        const d = rtcoPromoData?.disclaimer?.content;

        return hasRichTextBody(d) ? d : null;
    }, [rtcoPromoData]);

    useEffect(() => {
        if (isOpen) {
            setIsLegalExpanded(false);
            setError(null);
        }
    }, [isOpen, rtcoPromoData?.context]);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const promoCode = rtcoPromoData?.promoCode;
        const appliedPromos = basket?.appliedPromotions || [];
        const nextIsApplied = Boolean(promoCode) && appliedPromos.some(p => p?.couponCode?.toLowerCase?.() === promoCode.toLowerCase());
        setIsApplied(nextIsApplied);
    }, [isOpen, basket?.appliedPromotions, rtcoPromoData?.promoCode]);

    useEffect(() => {
        let rafId;

        // Only measure overflow in the *collapsed* state; when expanded, it won't overflow
        // and we still want "Read Less" to remain visible.
        if (isOpen && disclaimerContent && !isLegalExpanded && typeof window !== 'undefined' && window.requestAnimationFrame) {
            // Detect if collapsed legal text is actually truncated before showing the toggle.
            // This is intentionally browser-only (post-render) for SSR safety.
            rafId = window.requestAnimationFrame(() => {
                const node = legalRef.current;

                if (!node) {
                    return;
                }

                // When collapsed, overflow means there is more content than we can display.
                // Tolerance accounts for subpixel rounding across browsers/zoom levels.
                const isOverflowing = node.scrollHeight > node.clientHeight + OVERFLOW_PX_TOLERANCE;
                setIsLegalExpandable(isOverflowing);
            });
        }

        return () => {
            if (rafId && typeof window !== 'undefined' && window.cancelAnimationFrame) {
                window.cancelAnimationFrame(rafId);
            }
        };
    }, [isOpen, disclaimerContent, isLegalExpanded]);

    if (!rtcoPromoData) {
        return null;
    }

    const { banner, modalSubtitle, modalEligibility, legalText } = rtcoPromoData;
    const bannerText = banner?.text;

    const handleApplyPromotion = async () => {
        const { promoCode } = rtcoPromoData || {};

        if (!promoCode) {
            return;
        }

        setIsApplying(true);
        setError(null);

        try {
            const result = await promoUtils.applyPromo(promoCode);

            // `promoUtils.applyPromo` returns (not throws) on errors in some flows.
            if (hasNonThrowingError(result)) {
                setError(result?.errorMessage || result?.errorMessages?.[0] || getText('applyError'));
            } else {
                dispatch(refreshBasket(true));
            }
        } catch (err) {
            Sephora.logger.error('Error applying RTCO promotion:', err);
            setError(err?.errorMessage || err?.errorMessages?.[0] || getText('applyError'));
        } finally {
            setIsApplying(false);
        }
    };

    const handleRemovePromotion = async () => {
        const { promoCode } = rtcoPromoData || {};

        if (!promoCode) {
            return;
        }

        setIsApplying(true);
        setError(null);

        try {
            const result = await promoUtils.removePromo(promoCode);

            if (hasNonThrowingError(result)) {
                setError(result?.errorMessage || result?.errorMessages?.[0] || getText('removeError'));
            } else {
                dispatch(refreshBasket(true));
            }
        } catch (err) {
            Sephora.logger.error('Error removing RTCO promotion:', err);
            setError(err?.errorMessage || err?.errorMessages?.[0] || getText('removeError'));
        } finally {
            setIsApplying(false);
        }
    };

    const customStyle = {
        inner: {
            width: '100%',
            margin: 'auto',
            display: 'flex',
            flexDirection: 'column',
            [mediaQueries.sm]: {
                width: '80%',
                borderRadius: radii[2]
            }
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onDismiss={onDismiss}
            width={3}
            showDismiss={true}
            hasBodyScroll={true}
            margin='auto'
            customStyle={customStyle}
        >
            <Modal.Header>
                <Modal.Title>{getText('offerDetailsTitle')}</Modal.Title>
            </Modal.Header>
            <Modal.Body
                hasBodyScroll={true}
                css={styles.modalBody}
            >
                {hasRichTextBody(bannerText) && (
                    <Box marginBottom={3}>
                        <RichText
                            content={bannerText}
                            style={styles.headline}
                        />
                    </Box>
                )}
                {modalSubtitle && (
                    <Text
                        is='p'
                        fontSize='sm'
                        marginBottom={3}
                        lineHeight='tight'
                        children={modalSubtitle}
                    />
                )}
                {modalEligibility && (
                    <Text
                        is='p'
                        fontSize='xs'
                        color='gray'
                        marginBottom={legalText ? 2 : 3}
                        lineHeight='tight'
                        css={{ whiteSpace: 'pre-wrap' }}
                        children={modalEligibility}
                    />
                )}
                {legalText && (
                    <Text
                        is='p'
                        fontSize='xs'
                        color='gray'
                        marginBottom={3}
                        lineHeight='tight'
                        children={legalText}
                    />
                )}
                {Boolean(disclaimerContent) && (
                    <Box>
                        <Box
                            id={LEGAL_TEXT_ID}
                            ref={legalRef}
                            css={isLegalExpanded ? styles.legalExpanded : styles.legalCollapsed}
                        >
                            <RichText
                                content={disclaimerContent}
                                style={styles.disclaimerRichText}
                            />
                        </Box>
                        {(isLegalExpandable || isLegalExpanded) && (
                            <Link
                                display='inline-block'
                                marginTop={2}
                                marginBottom={2}
                                fontSize='sm'
                                color='blue'
                                underline={true}
                                aria-expanded={isLegalExpanded}
                                aria-controls={LEGAL_TEXT_ID}
                                onClick={() => setIsLegalExpanded(v => !v)}
                                data-at={Sephora.debug.dataAt('rtco_offer_details_read_more')}
                                children={isLegalExpanded ? getText('readLess') : getText('readMore')}
                            />
                        )}
                    </Box>
                )}
                {error && (
                    <Box
                        role='alert'
                        aria-live='polite'
                        aria-atomic='true'
                        marginBottom={3}
                    >
                        <Text
                            fontSize='sm'
                            color='red'
                            lineHeight='tight'
                            children={error}
                        />
                    </Box>
                )}
            </Modal.Body>
            <Modal.Footer
                css={styles.footer}
                hasShadowBorderForSMUI={true}
            >
                {isApplied ? (
                    <Box width={['100%', null, 200]}>
                        <Box
                            width='100%'
                            paddingY={2}
                            css={{
                                border: `${borders[1]} ${colors.black}`,
                                borderRadius: radii.full,
                                backgroundColor: colors.white,
                                textAlign: 'center'
                            }}
                            data-at={Sephora.debug.dataAt('rtco_offer_details_applied')}
                        >
                            <Flex
                                alignItems='center'
                                justifyContent='center'
                                marginBottom={1}
                                width='100%'
                            >
                                <Icon
                                    name='checkmark'
                                    size={14}
                                    marginRight={1}
                                />
                                <Flex flexDirection='column'>
                                    <Text
                                        is='p'
                                        fontWeight='bold'
                                        children={getText('applied')}
                                    />
                                    <Link
                                        color='blue'
                                        display='block'
                                        width='100%'
                                        fontSize={fontSizes.sm}
                                        fontWeight='normal'
                                        css={{ textAlign: 'center' }}
                                        onClick={e => {
                                            e.preventDefault();
                                            handleRemovePromotion();
                                        }}
                                        data-at={Sephora.debug.dataAt('rtco_offer_details_remove')}
                                        children={isApplying ? getText('removing') : getText('remove')}
                                    />
                                </Flex>
                            </Flex>
                        </Box>
                    </Box>
                ) : (
                    <Button
                        variant='secondary'
                        width={['100%', 140]}
                        onClick={handleApplyPromotion}
                        disabled={isApplying || !rtcoPromoData?.promoCode}
                        data-at={Sephora.debug.dataAt('rtco_offer_details_apply')}
                        children={isApplying ? getText('applying') : getText('apply')}
                    />
                )}
            </Modal.Footer>
        </Modal>
    );
}

const styles = {
    modalBody: {
        paddingBottom: space[2]
    },
    headline: {
        fontSize: fontSizes.sm,
        lineHeight: lineHeights.tight,
        fontWeight: 'bold',
        '& p': {
            marginBottom: 0
        }
    },
    disclaimerRichText: {
        fontSize: fontSizes.xs,
        lineHeight: lineHeights.tight,
        color: colors.gray,
        '& p': {
            marginBottom: space[2]
        },
        '& p:last-child': {
            marginBottom: 0
        }
    },
    legalCollapsed: {
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 4
    },
    legalExpanded: {
        overflow: 'visible',
        display: 'block'
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        padding: space[4],
        backgroundColor: colors.white,
        boxShadow: '0 0 6px rgba(0, 0, 0, 0.2)'
    }
};

RtcoOfferDetailsModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onDismiss: PropTypes.func.isRequired,
    rtcoPromoData: PropTypes.object
};

export default wrapFunctionalComponent(RtcoOfferDetailsModal, 'RtcoOfferDetailsModal');
